import React from 'react';
import Table, { type TableModel } from './Table';

type Props = TableModel & {
  onChange: (next: TableModel) => void;
};

const TableSettings: React.FC<Props> = ({ rows, cols, data, colWidths, rowHeights, dense = false, bordered = true, fitCols = false, fitRows = false, scrollable = false, hoverOutline = true, onChange }) => {
  const ensureSize = (r: number, c: number) => {
    const dd = Array.from({ length: r }, (_, ri) => Array.from({ length: c }, (_, ci) => data?.[ri]?.[ci] ?? ''));
    const cw = Array.from({ length: c }, (_, i) => colWidths?.[i] ?? 0);
    const rh = Array.from({ length: r }, (_, i) => rowHeights?.[i] ?? 0);
    onChange({ rows: r, cols: c, data: dd, colWidths: cw, rowHeights: rh, dense, bordered, fitCols, fitRows, scrollable, hoverOutline });
  };

  const addRow = () => ensureSize(rows + 1, cols);
  const removeRow = () => ensureSize(Math.max(1, rows - 1), cols);
  const addCol = () => ensureSize(rows, cols + 1);
  const removeCol = () => ensureSize(rows, Math.max(1, cols - 1));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="text-sm font-medium text-gray-900">Table editor</div>
      </div>
      <div className="relative z-0 mb-3 rounded-lg border border-gray-200 p-2 bg-white overflow-hidden">
        <Table
          rows={rows}
          cols={cols}
          data={data}
          colWidths={colWidths}
          rowHeights={rowHeights}
          dense={dense}
          bordered={bordered}
          fitCols={fitCols}
          fitRows={fitRows}
          scrollable={scrollable}
          hoverOutline={hoverOutline}
          editable={true}
          showResizeHandles={false}
          onChange={onChange}
        />
      </div>
      <div className="relative z-10 grid grid-cols-2 sm:grid-cols-4 gap-2">
        <button className="rounded-md border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50" onClick={addRow}>
          + Row
        </button>
        <button className="rounded-md border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50" onClick={removeRow}>
          − Row
        </button>
        <button className="rounded-md border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50" onClick={addCol}>
          + Column
        </button>
        <button className="rounded-md border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50" onClick={removeCol}>
          − Column
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 p-3">
          <span className="text-sm text-gray-800">Dense spacing</span>
          <button
            type="button"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${dense ? 'bg-purple-600' : 'bg-gray-300'}`}
            onMouseDown={(e)=>{ e.stopPropagation(); }}
            onClick={() => onChange({ rows, cols, data, colWidths, rowHeights, dense: !dense, bordered, fitCols, fitRows, scrollable, hoverOutline })}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${dense ? 'translate-x-5' : 'translate-x-1'}`} />
          </button>
        </label>
        <label className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 p-3">
          <span className="text-sm text-gray-800">Show borders</span>
          <button
            type="button"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${bordered ? 'bg-purple-600' : 'bg-gray-300'}`}
            onMouseDown={(e)=>{ e.stopPropagation(); }}
            onClick={() => onChange({ rows, cols, data, colWidths, rowHeights, dense, bordered: !bordered, fitCols, fitRows, scrollable, hoverOutline })}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${bordered ? 'translate-x-5' : 'translate-x-1'}`} />
          </button>
        </label>
      </div>

      <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 p-3">
          <span className="text-sm text-gray-800">Fit columns to container</span>
          <button
            type="button"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${fitCols ? 'bg-purple-600' : 'bg-gray-300'}`}
            onMouseDown={(e)=>{ e.stopPropagation(); }}
            onClick={() => onChange({ rows, cols, data, colWidths, rowHeights, dense, bordered, fitCols: !fitCols, fitRows, scrollable, hoverOutline })}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${fitCols ? 'translate-x-5' : 'translate-x-1'}`} />
          </button>
        </label>
        <label className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 p-3">
          <span className="text-sm text-gray-800">Compact row height</span>
          <button
            type="button"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${fitRows ? 'bg-purple-600' : 'bg-gray-300'}`}
            onMouseDown={(e)=>{ e.stopPropagation(); }}
            onClick={() => onChange({ rows, cols, data, colWidths, rowHeights, dense, bordered, fitCols, fitRows: !fitRows, scrollable, hoverOutline })}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${fitRows ? 'translate-x-5' : 'translate-x-1'}`} />
          </button>
        </label>
      </div>

      <div className="relative z-10 grid grid-cols-1 gap-3">
        <label className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 p-3">
          <span className="text-sm text-gray-800">Allow scrolling</span>
          <button
            type="button"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${scrollable ? 'bg-purple-600' : 'bg-gray-300'}`}
            onMouseDown={(e)=>{ e.stopPropagation(); }}
            onClick={() => onChange({ rows, cols, data, colWidths, rowHeights, dense, bordered, fitCols, fitRows, scrollable: !scrollable })}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${scrollable ? 'translate-x-5' : 'translate-x-1'}`} />
          </button>
        </label>
        <label className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 p-3">
          <span className="text-sm text-gray-800">Show dotted outline</span>
          <button
            type="button"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${hoverOutline ? 'bg-purple-600' : 'bg-gray-300'}`}
            onMouseDown={(e)=>{ e.stopPropagation(); }}
            onClick={() => onChange({ rows, cols, data, colWidths, rowHeights, dense, bordered, fitCols, fitRows, scrollable, hoverOutline: !hoverOutline })}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${hoverOutline ? 'translate-x-5' : 'translate-x-1'}`} />
          </button>
        </label>
      </div>
    </div>
  );
};

export default TableSettings;
